import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../../common/Mypage.scss';
import MypageMenu from '../../common/MypageMenu';
import Loading from '../../../../components/Loading';
import {
  seekerApi,
  errorMessage,
  isConflict,
  type ApplicationStatusCode,
  type SeekerDetail,
} from '../../applications/applicationStatusApi';
import { ConfirmDialog, MessageThread, StatusBadge, Timeline } from '../../applications/applicationUi';

// 구직자 지원 상세 · 타임라인 (application-status-design.md 5장)

// 상태별 보조 안내 (5.1)
const GUIDE: Partial<Record<ApplicationStatusCode, { text: string; tone?: 'warn' }>> = {
  SUBMITTED: { text: '지원이 접수되었습니다. 교회가 사역자모아에서 이력서를 확인하면 이곳에 표시됩니다. (교회 메일함에서 본 경우는 알 수 없습니다)' },
  VIEWED: { text: '교회가 사역자모아에서 이력서를 확인했습니다. 검토 중입니다.' },
  ON_HOLD: { text: '교회 사정으로 검토가 길어지고 있습니다. 다른 공고도 함께 보시길 권합니다.', tone: 'warn' },
  ACCEPTED: { text: '축하합니다. 교회가 연락드릴 예정입니다.' },
  REJECTED: { text: '이번 청빙에서는 함께하지 못하게 되었습니다.' },
  WITHDRAWN: { text: '지원이 철회되었습니다. 교회에는 더 이상 표시되지 않습니다.' },
};

// 아직 일어나지 않은 단계 안내 (타임라인의 예정 노드)
function futureLabel(status: ApplicationStatusCode) {
  if (['SUBMITTED', 'VIEWED', 'ON_HOLD', 'CONTACTED'].includes(status)) return '면접 · 청빙 제안';
  if (status === 'INTERVIEW') return '청빙 제안';
  return null;
}

type Pending = 'withdraw' | 'accept' | 'decline' | null;

export default function ApplicationDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState<SeekerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setApp(await seekerApi.detail(id));
      setError('');
    } catch (e) {
      setError(errorMessage(e, '지원 내역을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const run = async () => {
    if (!app || !pending) return;
    setBusy(true);
    try {
      if (pending === 'withdraw') await seekerApi.withdraw(app.id, app.version);
      if (pending === 'accept') await seekerApi.accept(app.id, app.version);
      if (pending === 'decline') await seekerApi.decline(app.id, app.version);
      setPending(null);
      await load();
    } catch (e) {
      alert(errorMessage(e));
      setPending(null);
      if (isConflict(e)) await load();
    } finally {
      setBusy(false);
    }
  };

  const guide = app ? GUIDE[app.status] : undefined;
  const canMessage = Boolean(app?.actions.includes('MESSAGE'));

  return (
    <div className="mypage">
      <div className="inner">
        <MypageMenu />
        <div className="subpage__main">
          <div className="mypage-panel mypage-section-card">
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
                <Loading />
              </div>
            ) : error || !app ? (
              <div className="app-empty">
                <p>{error || '지원 내역을 찾을 수 없습니다.'}</p>
                <button type="button" className="app-btn" onClick={() => navigate('/mypage/applications')}>
                  지원현황으로
                </button>
              </div>
            ) : (
              <div className="app-detail">
                <div className="app-detail__head">
                  <div>
                    <h3>
                      {app.job.church ? `${app.job.church} · ` : ''}
                      {app.job.title}
                    </h3>
                    <p>
                      {[app.job.region, `지원 ${app.appliedAt.slice(0, 10).replace(/-/g, '.')}`].filter(Boolean).join(' · ')}
                      {app.job.closed && ' · 공고 마감'}
                    </p>
                  </div>
                  <StatusBadge status={app.status} />
                </div>

                {app.status === 'OFFERED' && (
                  <div className="app-detail__notice app-detail__notice--offer">
                    <strong>{app.job.church || '교회'}에서 청빙을 제안했습니다.</strong>
                    수락 시 교회에 즉시 알림이 전송됩니다.
                    <div className="app-detail__actions" style={{ borderTop: 'none', marginTop: 12, paddingTop: 0 }}>
                      <button type="button" className="app-btn" onClick={() => setPending('decline')}>
                        거절하기
                      </button>
                      <button type="button" className="app-btn app-btn--accent" onClick={() => setPending('accept')}>
                        수락하기
                      </button>
                    </div>
                  </div>
                )}
                {app.status === 'INTERVIEW' && app.interviewAt && (
                  <div className="app-detail__notice">면담 일시: {app.interviewAt}</div>
                )}
                {app.deliveryNote && (
                  <div className={`app-detail__notice${app.deliveryState === 'HELD' ? '' : ' app-detail__notice--warn'}`}>
                    {app.deliveryNote}
                    {app.originalUrl && (
                      <> <a href={app.originalUrl} target="_blank" rel="noopener noreferrer">원문 게시글 열기 ›</a></>
                    )}
                  </div>
                )}
                {guide && !app.deliveryNote && (
                  <div className={`app-detail__notice${guide.tone === 'warn' ? ' app-detail__notice--warn' : ''}`}>{guide.text}</div>
                )}

                <section className="app-detail__section">
                  <h4>진행 기록</h4>
                  <Timeline items={app.timeline} showFuture={futureLabel(app.status)} />
                  <p style={{ color: 'var(--ink-4)', fontSize: 15 }}>교회는 지원 시점의 이력서를 봅니다. 이후 수정한 이력서는 새 지원에만 적용됩니다.</p>
                </section>

                {(canMessage || app.messages.length > 0) && (
                  <section className="app-detail__section">
                    <h4>메시지</h4>
                    <MessageThread
                      applicationId={app.id}
                      messages={app.messages}
                      canSend={canMessage}
                      onSent={(m) => setApp({ ...app, messages: [...app.messages, m] })}
                    />
                  </section>
                )}

                {app.similar.length > 0 && (
                  <section className="app-detail__section">
                    <h4>비슷한 공고</h4>
                    <div className="app-similar">
                      {app.similar.map((s) => (
                        <Link key={s.path} to={s.path}>
                          <small>{[s.church, s.region].filter(Boolean).join(' · ')}</small>
                          {s.title}
                        </Link>
                      ))}
                    </div>
                  </section>
                )}

                <div className="app-detail__actions">
                  <button type="button" className="app-btn" onClick={() => navigate('/mypage/applications')}>
                    목록
                  </button>
                  {app.job.path && !app.job.deleted && (
                    <button type="button" className="app-btn" onClick={() => navigate(app.job.path!)}>
                      공고 원문 보기
                    </button>
                  )}
                  {app.actions.includes('WITHDRAW') && (
                    <button type="button" className="app-btn app-btn--danger-outline" onClick={() => setPending('withdraw')}>
                      지원취소
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {app && pending === 'withdraw' && (
        <ConfirmDialog title="지원을 취소할까요?" confirmLabel="지원취소" cancelLabel="계속 유지하기" danger busy={busy} onConfirm={run} onCancel={() => setPending(null)}>
          {app.wasViewed && <p>{app.job.church || '교회'}가 이미 사역자모아에서 이력서를 확인했습니다.</p>}
          <p>취소하면 교회에는 더 이상 진행 대상으로 표시되지 않습니다.</p>
          <p>다시 지원하려면 공고가 게시 중이어야 합니다.</p>
        </ConfirmDialog>
      )}
      {app && pending === 'accept' && (
        <ConfirmDialog title="청빙 제안을 수락할까요?" confirmLabel="수락하기" busy={busy} onConfirm={run} onCancel={() => setPending(null)}>
          <p>수락하면 {app.job.church || '교회'}에 즉시 알림이 전송되며 되돌릴 수 없습니다.</p>
        </ConfirmDialog>
      )}
      {app && pending === 'decline' && (
        <ConfirmDialog title="청빙 제안을 거절할까요?" confirmLabel="거절하기" danger busy={busy} onConfirm={run} onCancel={() => setPending(null)}>
          <p>거절하면 이 지원은 종료되고 교회에 알림이 전송됩니다.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
